import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';

interface TweetContestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TweetContestModal: React.FC<TweetContestModalProps> = ({ isOpen, onClose }) => {
  const [tweetUrl, setTweetUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submittedTweets, setSubmittedTweets] = useState<any[]>([]);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(false);

  // Блокировка прокрутки страницы при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString();

  useEffect(() => {
    if (isOpen && walletAddress) {
      setSuccess(false); // Reset success state when modal opens
      checkUserSubmission(walletAddress);
    }
  }, [isOpen, walletAddress]);

  // Управление анимациями открытия/закрытия
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready before animation
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const checkUserSubmission = async (address: string) => {
    try {
      const response = await fetch(`/api/tweet-contest/${address}`);
      if (response.ok) {
        const tweets = await response.json();
        setSubmittedTweets(tweets);
      } else {
        setSubmittedTweets([]);
      }
    } catch (err) {
      setSubmittedTweets([]);
    }
  };

  const handleSubmit = async () => {
    if (!tweetUrl.trim() || !walletAddress) {
      setError('Please enter a tweet URL and connect your wallet');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/tweet-contest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wallet_address: walletAddress,
          tweet_url: tweetUrl.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit tweet');
      }

      const newTweet = await response.json();
      setSubmittedTweets(prev => [newTweet, ...prev]); // Add new tweet to the beginning of the list
      setTweetUrl(''); // Clear the input field
      setSuccess(true);

      // Reset success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit tweet');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-50">
      <style>{`
        .modal-content {
          opacity: 0;
          transform: scale(0.5);
          transition: all 0.15s ease-out;
        }

        .modal-content.entering {
          opacity: 1;
          transform: scale(1);
        }

        .modal-content.exiting {
          opacity: 0;
          transform: scale(0.5);
        }
      `}</style>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="fixed inset-0 flex items-center justify-center">
        <div
          className={`bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-border-glow modal-content ${
            isAnimating ? 'entering' : 'exiting'
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold text-white">Arcium x Arcane Hands Contest</h2>
            <button
              onClick={onClose}
              className="text-purple-400 hover:text-white transition-colors p-1 hover:btn-textured-secondary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Contest Description */}
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-3">How to participate:</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Follow <a href="https://x.com/ArciumHQ" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">@ArciumHQ</a>, <a href="https://x.com/rodionadov" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">@adovrn</a> and <a href="https://x.com/deundef1ned" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">@deundef1ned</a></span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Create a unique and interesting tweet about Arcium and Arcane Hands</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span><span>Must mention <a href="https://x.com/ArciumHQ" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">@ArciumHQ</a>, <a href="https://x.com/rodionadov" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">@adovrn</a> and <a href="https://x.com/deundef1ned" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">@deundef1ned</a>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-2">•</span>
                    <span>Please make sure to provide a link to your tweet below</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-3">Prizes:</h3>
                <div className="space-y-1 text-gray-300">
                  <p><span className="text-yellow-400 font-bold">🥇</span> - 75 USDC</p>
                  <p><span className="text-gray-400 font-bold">🥈</span> - 35 USDC</p>
                  <p><span className="text-gray-400 font-bold">🥉</span> - 25 USDC</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-3">Winners selection criteria:</h3>
                <p className="text-gray-300">Winners determined by highest views, likes, retweets, replies combined</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-3">Deadline:</h3>
                <p className="text-gray-300">The contest will last for 7 days, starting on October 6. The results of the contest will be presented on this website on October 13</p>
              </div>
            </div>

            {/* Submitted Tweets List */}
            {submittedTweets.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-white">Your submitted tweets:</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {submittedTweets.map((tweet, index) => (
                    <div key={tweet.id} className="bg-purple-900/30 border border-purple-600/30 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <a
                          href={tweet.tweet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-400 hover:text-purple-300 underline text-sm truncate flex-1 mr-2"
                        >
                          {tweet.tweet_url}
                        </a>
                        <span className="text-gray-400 text-xs">
                          {new Date(tweet.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tweet URL Input */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-300">
                Tweet link
              </label>
              <input
                type="url"
                value={tweetUrl}
                onChange={(e) => setTweetUrl(e.target.value)}
                placeholder="https://x.com/username/status/..."
                disabled={!walletAddress}
                className="w-full px-4 py-3 rounded-lg border bg-black/50 border-purple-600/50 text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
              />
              {success && (
                <p className="text-green-400 text-sm">Tweet submitted successfully! Good luck in the contest!</p>
              )}
              {!walletAddress && (
                <p className="text-sm text-yellow-500">Please connect your wallet to participate</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!tweetUrl.trim() || isSubmitting || !walletAddress}
              className={`w-full py-3 px-4 border-2 border-purple-500/80 font-medium ${
                tweetUrl.trim() && walletAddress && !isSubmitting
                  ? 'btn-textured text-white'
                  : 'btn-textured-secondary text-white/50 cursor-not-allowed opacity-50'
              } transition-colors`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Tweet'}
            </button>

            {/* Error Message */}
            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-700/50">
              <p className="text-gray-400 text-xs leading-relaxed">
                Make sure your tweet follows all the participation rules. Only one submission per wallet address is allowed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
